import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook } from 'lucide-react';
import { getContactInfo } from '../utils/contentManager';

const Footer = () => {
  const [contactData, setContactData] = useState({});

  useEffect(() => {
    const contact = getContactInfo();
    setContactData(contact);
  }, []);

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Case Studies', path: '/case-studies' }
  ];

  const services = [
    'IT Consulting',
    'Business Strategy',
    'Agentic AI Solutions',
    'Digital Transformation'
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-2xl font-bold text-gradient"
            >
              OHA System
            </motion.div>
            <p className="text-gray-300 leading-relaxed">
              Transforming small businesses and startups with cutting-edge Agentic AI solutions and strategic consulting.
            </p>
            <div className="flex space-x-4">
              {[
                { Icon: Linkedin, href: contactData.socialMedia?.linkedin || '#' },
                { Icon: Twitter, href: contactData.socialMedia?.twitter || '#' },
                { Icon: Facebook, href: contactData.socialMedia?.facebook || '#' }
              ].map(({ Icon, href }, index) => (
                <motion.a
                  key={index}
                  whileHover={{ scale: 1.2, y: -2 }}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-blue-800 hover:bg-blue-900 transition-colors"
                >
                  <Icon size={20} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service} className="text-gray-300">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail size={20} className="text-blue-800" />
                <span className="text-gray-300">{contactData.email || 'contact@ohasystem.com'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={20} className="text-blue-800" />
                <span className="text-gray-300">{contactData.phone || '+1 (555) 123-4567'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin size={20} className="text-blue-800" />
                <span className="text-gray-300">{contactData.address || 'San Francisco, CA'}</span>
              </div>
            </div>
            
            {/* Newsletter Signup */}
            <div className="space-y-3">
              <h4 className="font-semibold">Stay Updated</h4>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 rounded-l-lg bg-gray-800 border border-gray-700 focus:outline-none focus:border-blue-800 text-white"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="gradient-background px-4 py-2 rounded-r-lg font-semibold"
                >
                  Subscribe
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © 2024 OHA System. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="#" className="text-gray-400 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="#" className="text-gray-400 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;